import React, { useState } from 'react';
import { User, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';

interface ClaimSequenceButtonProps {
  sequenceId: string;
  sequenceTitle: string;
  sequenceCreatorId?: string;
  className?: string;
  variant?: 'badge' | 'button' | 'inline';
}

const SAINT_NICK_USER_ID = 'f47ac10b-58cc-4372-a567-0e02b2c3d479';

const ClaimSequenceButton: React.FC<ClaimSequenceButtonProps> = ({
  sequenceId,
  sequenceTitle,
  sequenceCreatorId,
  className = '',
  variant = 'badge'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    contactEmail: '',
    verificationDetails: ''
  });
  
  const { user } = useAuth();
  const { toast } = useToast();

  // Only show if this sequence is owned by Saint Nick
  const isClaimable = sequenceCreatorId === SAINT_NICK_USER_ID;
  
  if (!isClaimable) {
    return null;
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to claim ownership of this sequence.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.contactEmail.trim()) {
      toast({
        title: "Email Required",
        description: "Please provide a contact email address.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.verificationDetails.trim()) {
      toast({
        title: "Verification Details Required", 
        description: "Please provide details to verify your ownership of this sequence.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Check if user already has a pending claim for this sequence
      const { data: existingClaim, error: checkError } = await supabase
        .from('ownership_claims')
        .select('id, claim_status')
        .eq('sequence_id', sequenceId)
        .eq('claimant_user_id', user.id)
        .single();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        throw checkError;
      }

      if (existingClaim) {
        toast({
          title: "Claim Already Exists",
          description: `You already have a ${existingClaim.claim_status} claim for this sequence.`,
          variant: "default"
        });
        setIsOpen(false);
        return;
      }

      // Submit the ownership claim
      const { error: insertError } = await supabase
        .from('ownership_claims')
        .insert({
          sequence_id: sequenceId,
          claimant_user_id: user.id,
          contact_email: formData.contactEmail.trim(),
          verification_details: formData.verificationDetails.trim(),
          claim_status: 'pending'
        });

      if (insertError) {
        throw insertError;
      }

      toast({
        title: "Claim Submitted Successfully",
        description: "Your ownership claim has been submitted for review. You'll be notified of the decision via email.",
        variant: "default"
      });

      // Reset form and close dialog
      setFormData({ contactEmail: '', verificationDetails: '' });
      setIsOpen(false);

    } catch (error) {
      console.error('Error submitting claim:', error);
      toast({
        title: "Failed to Submit Claim",
        description: error instanceof Error ? error.message : "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTrigger = () => {
    switch (variant) {
      case 'button':
        return (
          <Button 
            variant="outline" 
            size="sm"
            className={cn("gap-2", className)}
          >
            <User size={16} />
            Claim Ownership
          </Button>
        );
      
      case 'inline':
        return (
          <button 
            className={cn(
              "text-sm text-blue-600 hover:text-blue-800 underline flex items-center gap-1 transition-colors",
              className
            )}
          >
            <User size={14} />
            Claim this sequence
          </button>
        );
      
      case 'badge':
      default:
        return (
          <Badge 
            variant="secondary" 
            className={cn(
              "cursor-pointer hover:bg-blue-100 hover:text-blue-800 transition-colors gap-1 text-xs",
              className
            )}
          >
            <User size={12} />
            Claimable
          </Badge>
        );
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {renderTrigger()}
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User size={20} />
            Claim Sequence Ownership
          </DialogTitle>
          <DialogDescription>
            You're claiming ownership of "<strong>{sequenceTitle}</strong>". 
            Please provide verification details to help our team confirm your ownership.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmitClaim} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="contactEmail">Contact Email *</Label>
            <Input
              id="contactEmail"
              type="email"
              placeholder="your.email@example.com"
              value={formData.contactEmail}
              onChange={(e) => handleInputChange('contactEmail', e.target.value)}
              required
              disabled={isSubmitting}
            />
            <p className="text-xs text-muted-foreground">
              This email will be used for communication about your claim.
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="verificationDetails">Verification Details *</Label>
            <Textarea
              id="verificationDetails"
              placeholder="Please provide details that help verify your ownership, such as:
- Original creation date
- Software used to create the sequence  
- Display location where it was used
- Social media links or posts
- Any other proof of authorship"
              value={formData.verificationDetails}
              onChange={(e) => handleInputChange('verificationDetails', e.target.value)}
              required
              disabled={isSubmitting}
              rows={6}
            />
          </div>

          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
            <AlertCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Claim Review Process</p>
              <p>Our team will review your claim and may contact you for additional verification. You'll receive an email notification once a decision has been made.</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Claim'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ClaimSequenceButton; 